import { useTranslation } from "react-i18next";
import { bffApi, type PersonDto } from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { useConfirmationModal } from "../../../../common/useConfirmationModal";
import { displayPersonNameWithEmail } from "../../../../common/persons/displayPersonName";

interface Delegation {
  responsible: PersonDto;
  taskId?: string;
  delegate: () => Promise<void> | void;
  onCancelled?: () => void;
}

export const useTaskDelegationConfirmation = (contractId: string) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const confirmationModal = useConfirmationModal();
  const { trigger } = useApiError();

  const keepsAccess = async (responsible: PersonDto, taskId?: string) => {
    const query = dispatch(
      bffApi.endpoints.getContractTaskDelegation.initiate(
        { contractId, responsiblePersonId: responsible.id, taskId },
        { forceRefetch: true },
      ),
    );
    try {
      return await query.unwrap();
    } finally {
      query.unsubscribe();
    }
  };

  const delegateConfirmingLoss = async ({ responsible, taskId, delegate, onCancelled }: Delegation) => {
    let answer;
    try {
      answer = await keepsAccess(responsible, taskId);
    } catch (e) {
      trigger(e);
      onCancelled?.();
      return;
    }
    if (answer.keepsAccess) {
      await delegate();
      return;
    }
    confirmationModal.show({
      title: t("Delegate the task to {{name}}?", { name: displayPersonNameWithEmail(responsible) }),
      description: t(
        "Once the task is delegated to someone outside {{organization}}, you can no longer edit it, change its status or comment on it.",
        { organization: answer.actingOrganizationName },
      ),
      confirmButtonText: t("Delegate the task") ?? "",
      onConfirmed: delegate,
      onCancelled,
    });
  };

  return { delegateConfirmingLoss, modal: confirmationModal.modal };
};
